import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { partnerBankAccountsApi } from '../../api/partner-contacts-offices-bank';
import { ApiError } from '../../api/client';
import { usePermission } from '../../auth/AuthContext';
import { DataTable } from '../../components/DataTable';
import { IconActionButton } from '../../components/IconActionButton';
import { Check } from 'lucide-react';
import { useToast } from '../../components/Toast';

/**
 * Restricted (spec section 14) - genuinely hidden client-side for
 * anyone without partner.due_diligence.bank_info.view, on top of the
 * real server-side enforcement. Never shown as a tab at all to an
 * unauthorized user, not merely disabled.
 */
export function PartnerBankInfoTab({ partnerId }: { partnerId: number }) {
  const { showSuccess, showError } = useToast();
  const queryClient = useQueryClient();
  const canManage = usePermission('partner.due_diligence.bank_info.manage');
  const bankAccountsQuery = useQuery({ queryKey: ['partner-bank-accounts', partnerId], queryFn: () => partnerBankAccountsApi.list(partnerId) });
  const { register, handleSubmit, reset } = useForm<{ bankName: string; accountName: string; accountNumber: string; currency: string; branch: string; swiftCode: string }>({ defaultValues: { currency: 'NGN' } });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['partner-bank-accounts', partnerId] });
  const createMutation = useMutation({
    mutationFn: (values: Record<string, string>) => partnerBankAccountsApi.create(partnerId, values),
    onSuccess: () => { showSuccess('Bank account added.'); invalidate(); reset(); },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed to add bank account.'),
  });
  const verifyMutation = useMutation({ mutationFn: (id: number) => partnerBankAccountsApi.verify(id), onSuccess: () => { showSuccess('Verified.'); invalidate(); }, onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed.') });

  return (
    <div className="panel">
      <div className="panel-body">
        {bankAccountsQuery.data && (
          <DataTable
            rows={bankAccountsQuery.data.data}
            rowKey={(b) => b.id}
            emptyMessage="No bank accounts on file yet."
            columns={[
              { header: 'Bank', render: (b) => b.bank_name },
              { header: 'Account Name', render: (b) => b.account_name },
              { header: 'Account Number', render: (b) => b.account_number },
              { header: 'Currency', render: (b) => b.currency },
              { header: 'Branch', render: (b) => b.branch ?? '-' },
              { header: 'Verification', render: (b) => b.verification_status === 'verified' ? 'Verified' : 'Unverified' },
              { header: '', render: (b) => canManage && b.verification_status !== 'verified' && <IconActionButton icon={Check} label="Verify" onClick={() => verifyMutation.mutate(b.id)} /> },
            ]}
          />
        )}

        {canManage && (
          <>
            <h3>Add Bank Account</h3>
            <form className="inline-form" onSubmit={handleSubmit((values) => createMutation.mutate(values))}>
              <label>Bank Name<input {...register('bankName', { required: true })} /></label>
              <label>Account Name<input {...register('accountName', { required: true })} /></label>
              <label>Account Number<input {...register('accountNumber', { required: true })} /></label>
              <label>Currency<input {...register('currency', { required: true })} /></label>
              <label>Branch<input {...register('branch')} /></label>
              <label>SWIFT<input {...register('swiftCode')} /></label>
              <button type="submit" disabled={createMutation.isPending}>{createMutation.isPending ? 'Adding…' : 'Add'}</button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
